/**
 * TutorCloseOutScreen — le bilan de séance du masteur (`end-session-tutor?occurrence_id=`), hors
 * live : depuis la prescription du champion (« Continuer vers le bilan », avec ce qu'elle a prescrit), et
 * depuis la tâche « Comptes-rendus de lives manquants » de « Ma journée ». Dans la pile du live,
 * l'ancien écran reste (le live n'est pas câblé) — il quitte aussi le live, ce que celui-ci n'a pas
 * à faire.
 *
 * Un fil : les lectures, les brouillons et les gestes, le view-model, la page — un vocal s'y
 * enregistre sur place, dans sa carte (`useTutorCloseOutRecorder`) — et, au-dessus, la confirmation
 * d'un ressenti qu'on efface.
 */
import React, { useMemo } from "react"
import { useSelector } from "react-redux"

import { ConfirmDialog } from "../../../../../../design-system"
import { TutorCloseOutPage } from "../../../../../../design-system/tutor"
import { type ApiMissionPrescription } from "../../../../adapters/tutor/Lives/tutorCloseOutAdapter"
import {
    tutorCloseOutTagCatalogs,
    tutorCloseOutViewModel,
} from "../../../../view-models/tutor/Lives/tutorCloseOutViewModel"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import { useTutorCloseOutData } from "./useTutorCloseOutData"
import { useTutorCloseOutRecorder } from "./useTutorCloseOutRecorder"
import { useTutorCloseOutState } from "./useTutorCloseOutState"
import { useTutorCloseOutVoices } from "./useTutorCloseOutVoices"

type Params = {
    occurrence_id?: string | number | null
    liveSessionId?: string | number | null
    studyMissionPrescriptions?: Record<string, ApiMissionPrescription | null | undefined> | null
}

export function TutorCloseOutScreen({ route }: TutorSpaceScreenProps) {
    const params = (route?.params ?? {}) as Params
    const occurrenceId = params.occurrence_id ?? null
    const data = useTutorCloseOutData(occurrenceId)
    const catalogs = useSelector(tutorCloseOutTagCatalogs)
    const state = useTutorCloseOutState({
        occurrenceId,
        liveSessionId: params.liveSessionId ?? null,
        occurrence: data.occurrence,
        catalogs,
    })
    const vm = useMemo(
        () =>
            tutorCloseOutViewModel({
                closure: data.closure,
                occurrence: data.occurrence,
                drafts: state.drafts,
                prescriptions: params.studyMissionPrescriptions,
                catalogs,
            }),
        [data.closure, data.occurrence, state.drafts, params.studyMissionPrescriptions, catalogs]
    )

    const voices = useTutorCloseOutVoices(vm)
    const recorder = useTutorCloseOutRecorder(state.recorder)

    return (
        <>
            <TutorCloseOutPage
                dateLabel={vm.dateLabel}
                loading={data.loading}
                error={data.error}
                onRetry={data.refetch}
                proxies={vm.proxies}
                noStudentAttended={vm.noStudentAttended}
                students={vm.students}
                platform={vm.platform}
                playingVoiceId={voices.playingVoiceId}
                playingVoiceProgress={voices.playingVoiceProgress}
                onToggleStudentVoicePlay={voices.onToggleStudentVoicePlay}
                onTogglePlatformVoicePlay={voices.onTogglePlatformVoicePlay}
                {...state.gestures}
                voiceRecorder={recorder.props}
                onRecordStudentVoice={(id) => recorder.open(() => state.gestures.onRecordStudentVoice(id))}
                onRecordPlatformVoice={() => recorder.open(state.gestures.onRecordPlatformVoice)}
                testID="tutor-close-out"
            />
            <ConfirmDialog
                visible={state.platformModeConfirm.visible}
                title="Attention !"
                message={`Ce que tu as ${state.platformModeConfirm.spoken ? "enregistré" : "écrit"} sera supprimé.`}
                confirmLabel="Valider"
                onConfirm={state.platformModeConfirm.onConfirm}
                cancelLabel="Annuler"
                onCancel={state.platformModeConfirm.onCancel}
                onDismiss={state.platformModeConfirm.onCancel}
            />
        </>
    )
}
